'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import { SceneShelfContext, type SceneShelfContextValue } from './context.js'
import type { SceneProductCardData, PriceData, ButtonFunctionType } from './types.js'

export interface SceneShelfSceneProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 是否展示标签 */
  showTags?: boolean
  /** 是否展示原价 */
  showOriginalPrice?: boolean
  /** 语义化类名 */
  classNames?: SceneShelfContextValue['classNames']
  /** 产品选中状态变化回调 */
  onSelectionChange?: (selectedProducts: SceneProductCardData[]) => void
  /** 点击了解更多回调 */
  onLearnMore?: (selectedProducts: SceneProductCardData[]) => void | Promise<void>
  /** 点击立即购买回调 */
  onShopNow?: (selectedProducts: SceneProductCardData[]) => void | Promise<void>
  /** 点击添加到购物车回调 */
  onAddToCart?: (selectedProducts: SceneProductCardData[]) => void | Promise<void>
  /** 主要按钮文本 */
  primaryButtonText?: string
  /** 主要按钮功能类型 */
  primaryButtonFun?: ButtonFunctionType
  /** 次要按钮文本 */
  secondaryButtonText?: string
  /** 次要按钮功能类型 */
  secondaryButtonFun?: ButtonFunctionType
  /** 总价标签 */
  totalPriceLabel?: string
  /** 当前总价 */
  totalCurrentPrice?: PriceData
  /** 原总价 */
  totalOriginalPrice?: PriceData
  /** 初始选中的产品列表（通过 defaultSelected 标记） */
  products?: SceneProductCardData[]
  /** 子组件 */
  children: React.ReactNode
  /** 默认选中策略 first|all|none */
  defaultSelect?: 'first' | 'all' | 'none'
}

/**
 * SceneShelf Scene 组件
 *
 * @description 单个场景容器，提供 Context 配置和选择状态管理
 */
const SceneShelfScene = React.forwardRef<HTMLDivElement, SceneShelfSceneProps>(
  (
    {
      showTags = true,
      showOriginalPrice = true,
      classNames,
      onSelectionChange,
      onLearnMore,
      onShopNow,
      onAddToCart,
      primaryButtonText = 'Shop Now',
      primaryButtonFun = 'buyNow',
      secondaryButtonText = 'Add to Cart',
      secondaryButtonFun = 'addCart',
      totalPriceLabel = 'Total Price:',
      totalCurrentPrice,
      totalOriginalPrice,
      products = [],
      className,
      children,
      defaultSelect,
      ...props
    },
    ref
  ) => {
    // 管理选中状态
    const [selectedIds, setSelectedIds] = React.useState<Set<string>>(() => {
      const initialSelected = new Set<string>()
      products.forEach(product => {
        if (product.defaultSelected) {
          initialSelected.add(product.id)
        }
      })
      return initialSelected
    })

    // 计算选中的产品
    const selectedProducts = React.useMemo(() => {
      return products.filter(product => selectedIds.has(product.id))
    }, [products, selectedIds])

    // 处理选中状态变化
    const handleSelectChange = React.useCallback((selected: boolean, product: SceneProductCardData) => {
      setSelectedIds(prev => {
        const newSet = new Set(prev)
        if (selected) {
          newSet.add(product.id)
        } else {
          newSet.delete(product.id)
        }
        return newSet
      })
    }, [])

    // 通知父组件选中状态变化
    React.useEffect(() => {
      onSelectionChange?.(selectedProducts)
    }, [selectedProducts, onSelectionChange])

    const contextValue = React.useMemo<SceneShelfContextValue>(
      () => ({
        showTags,
        showOriginalPrice,
        classNames,
        selectedIds,
        selectedProducts,
        onSelectChange: handleSelectChange,
        onLearnMore,
        onShopNow,
        onAddToCart,
        primaryButtonText,
        primaryButtonFun,
        secondaryButtonText,
        secondaryButtonFun,
        totalPriceLabel,
        totalCurrentPrice,
        totalOriginalPrice,
      }),
      [
        showTags,
        showOriginalPrice,
        classNames,
        selectedIds,
        selectedProducts,
        handleSelectChange,
        onLearnMore,
        onShopNow,
        onAddToCart,
        primaryButtonText,
        primaryButtonFun,
        secondaryButtonText,
        secondaryButtonFun,
        totalPriceLabel,
        totalCurrentPrice,
        totalOriginalPrice,
      ]
    )

    return (
      <SceneShelfContext.Provider value={contextValue}>
        <div
          ref={ref}
          className={cn(
            'rounded-box laptop:flex-row lg-desktop:min-h-[560px] desktop:h-[448px] laptop:min-h-[448px] flex min-h-[560px]  w-full flex-col items-stretch gap-0 overflow-hidden',
            className,
            classNames?.scene
          )}
          {...props}
        >
          {children}
        </div>
      </SceneShelfContext.Provider>
    )
  }
)

SceneShelfScene.displayName = 'SceneShelf.Scene'

export { SceneShelfScene }
